import { isValid, parse, parseISO } from "date-fns";
import { fromZonedTime } from "date-fns-tz";

/**
 * Formats a date string into a Date object.
 *
 * @param value The date string to be formatted. It can be in the format of MM/DD/YYYY, MM/DD/YY, or any other format that can be parsed by date-fns.
 * @returns A Date object representing the parsed date. A two-digit year will be interpreted as the current century minus 100 years if the date is in the future.
 */
export const getDateFromString = (value: string): Date => {
    const dateString = value
        .split(/\D+/)
        .filter(Boolean)
        .map((part) => part.padStart(2, "0"))
        .join("");

    let date: Date;

    if (dateString.length >= 8) {
        date = parse(dateString.slice(0, 8), "MMddyyyy", new Date());
    } else if (dateString.length >= 6) {
        date = parse(dateString.slice(0, 6), "MMddyy", new Date());
    } else {
        return;
    }

    const now = new Date();
    if (date > now) {
        date.setFullYear(date.getFullYear() - 100);
    }

    return date;
};

/**
 * Attempts to parse a date string into a Date object.
 *
 * @param value - The date string to be parsed.
 * @returns A Date object if the parsing is successful, otherwise null.
 */
export const tryParseDate = (value?: string): Date | null => {
    if (!value) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-").map(Number);
        return new Date(year, month - 1, day);
    }

    const d = new Date(value);
    return isValid(d) ? d : null;
};

/**
 * Parses an ISO date string into a Date, treating zone-less values as UTC.
 *
 * @param value - The ISO date string to parse.
 * @returns A Date representing the instant. Strings with an explicit offset or
 * `Z` are honored as-is; zone-less and date-only strings are interpreted as UTC.
 */
export const parseUtcDate = (value: string): Date => {
    const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
    return hasZone ? parseISO(value) : fromZonedTime(value, "UTC");
};
